import { useCallback, useEffect, useState } from 'react';
import { AppUpdate, FlexibleUpdateInstallStatus } from '@capawesome/capacitor-app-update';
import type { PluginListenerHandle } from '@capacitor/core';
import { useAuth } from '@/context/auth/AuthContext';
import {
  getInfo,
  performImmediate,
  mapError,
  AppUpdateError,
} from '@/services/app/inAppUpdate.service';
import {
  fetchVersionInfo,
  getCurrentTrack,
  openStorePage,
  VersionInfo,
} from '@/services/app/app-update.service';
import { APP_VERSION } from '@/version';
import { compareVersions } from 'compare-versions';
import { useToast } from './use-toast';

const STATUS_KEY = 'app_update_status';
const ERROR_KEY = 'app_update_error';
const VERSION_KEY = 'app_update_version';
type Status = 'ready' | 'prep' | 'downloading' | 'verifying' | 'error';

/**
 * Implementazione Android dell'hook `useAppUpdate` basata sul plugin `@capawesome/capacitor-app-update`.
 */
export function useAppUpdate(mode: 'immediate' | 'flexible' = 'immediate') {
  void mode; // param kept for API parity with iOS
  const [visible, setVisible] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [mandatory, setMandatory] = useState(false);
  const [changelog, setChangelog] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('ready');
  const [progress, setProgress] = useState<number | undefined>(undefined);
  const { toast } = useToast();
  const { user } = useAuth();

  const clear = useCallback(() => {
    localStorage.removeItem(STATUS_KEY);
    localStorage.removeItem(ERROR_KEY);
    localStorage.removeItem(VERSION_KEY);
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    setStatus('ready');
    setProgress(undefined);
    setVisible(false);
  }, []);

  const startUpdate = useCallback(async () => {
    setVisible(true);
    const info = await getInfo().catch((error) => {
      console.debug('In-app update info unavailable', error);
      return null;
    });
    if (info) {
      console.debug('In-app update info', info);
    }
    if (!info?.updateAvailable) {
      await openStorePage(link || undefined);
      clear();
      return;
    }
    toast({ description: 'Preparazione' });
    setStatus('prep');
    setProgress(0);
    localStorage.setItem(STATUS_KEY, 'prep');
    localStorage.removeItem(ERROR_KEY);
    let handle: PluginListenerHandle | undefined;
    try {
      handle = await AppUpdate.addListener(
        'onFlexibleUpdateStateChange',
        ({ installStatus, bytesDownloaded, totalBytesToDownload }) => {
          if (installStatus === FlexibleUpdateInstallStatus.DOWNLOADING) {
            setStatus('downloading');
            const pct =
              bytesDownloaded !== undefined && totalBytesToDownload
                ? (bytesDownloaded / totalBytesToDownload) * 100
                : undefined;
            setProgress(pct);
            localStorage.setItem(STATUS_KEY, 'downloading');
            toast({ description: 'Download…' });
          } else if (installStatus === FlexibleUpdateInstallStatus.INSTALLING) {
            setStatus('verifying');
            setProgress(100);
            localStorage.setItem(STATUS_KEY, 'verifying');
            toast({ description: 'Verifica…' });
          }
        }
      );
      await performImmediate();
      clear();
    } catch (err) {
      const msg =
        mapError(err) === AppUpdateError.NETWORK_ERROR
          ? 'Errore di rete'
          : mapError(err) === AppUpdateError.INSUFFICIENT_STORAGE
          ? 'Spazio insufficiente'
          : 'Errore interno';
      clear();
      setStatus('error');
      setVisible(true);
      toast({ description: msg, variant: 'destructive' });
    } finally {
      await handle?.remove();
    }
  }, [toast, clear, link]);

  useEffect(() => {
    if (!user) {
      setVisible(false);
      return;
    }
    (async () => {
      const native = await getInfo().catch((error) => {
        console.debug('Unable to retrieve in-app update info', error);
        return null;
      });
      if (native) {
        console.debug('In-app update availability', native);
      }
      const info = await fetchVersionInfo(getCurrentTrack()).catch(() => null);
      if (info) {
        setLatestVersion(info.version);
        setMandatory(info.mandatory);
        setChangelog(info.changelog || null);
        setLink(info.link || null);
        // Gate nativo (#1402): il banner compare solo se Google Play conferma un
        // update installabile per questa track/device, non al solo bump di
        // version.json — che viene deployato in CI prima della propagazione su
        // Play e portava a una pagina store senza pulsante di aggiornamento.
        if (native?.updateAvailable && compareVersions(info.version, APP_VERSION) > 0) {
          setStatus('ready');
          setVisible(true);
        }
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const handler = async (e: Event) => {
      const { version, mandatory, changelog, link }: VersionInfo = (
        e as CustomEvent<VersionInfo>
      ).detail;
      const native = await getInfo().catch((error) => {
        console.debug('Unable to retrieve in-app update info (event)', error);
        return null;
      });
      if (native) {
        console.debug('In-app update availability (event)', native);
      }
      const isDev = Boolean(
        (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV
      );
      if (isDev || compareVersions(version, APP_VERSION) > 0) {
        setLatestVersion(version);
        setMandatory(mandatory);
        setChangelog(changelog || null);
        setLink(link || null);
        setStatus('ready');
        // Gate nativo (#1402): aggiorna i metadati sempre, ma mostra il banner
        // solo quando Google Play conferma l'update (o in dev per i test).
        if (isDev || native?.updateAvailable) {
          setVisible(true);
        }
      }
    };
    window.addEventListener('appUpdateAvailable', handler);
    return () => {
      window.removeEventListener('appUpdateAvailable', handler);
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const storedVersion = localStorage.getItem(VERSION_KEY);
    if (storedVersion !== APP_VERSION) {
      clear();
    }
    const storedStatus = localStorage.getItem(STATUS_KEY) as Status | null;
    const storedError = localStorage.getItem(ERROR_KEY);
    if (storedStatus) {
      setStatus(storedStatus);
      setVisible(true);
      if (storedStatus === 'error') {
        if (storedError) toast({ description: storedError, variant: 'destructive' });
      } else if (storedStatus !== 'ready') {
        startUpdate();
      }
    }
  }, [clear, startUpdate, toast, user]);

  const dismiss = useCallback(() => {
    if (mandatory) return;
    setVisible(false);
  }, [mandatory]);

  return {
    visible,
    latestVersion,
    mandatory,
    changelog,
    status,
    progress,
    startUpdate,
    dismiss,
    clear,
  };
}
